import { useEffect, useRef, useState } from "react";
import { useDict } from "@/i18n/useDict";

const AICallsSection = () => {
  const { dict } = useDict();
  const a = dict.aiCalls;
  const ref = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".animate-on-scroll").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => setDuration(audio.duration || 0);
    const onEnd = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const onSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const t = Number(e.target.value);
    audio.currentTime = t;
    setCurrentTime(t);
  };

  const fmt = (s: number) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const featureIcons = ["📞", "🗣️", "📉", "🕘"];
  const features = a.features.map((f, i) => ({ ...f, icon: featureIcons[i] }));

  return (
    <section id="aicalls" className="py-20 md:py-32 px-6 relative overflow-hidden scroll-mt-20">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-purple-500/5 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: audio demo player */}
          <div className="animate-on-scroll order-2 lg:order-1">
            <div
              className="rounded-2xl p-8 relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #1a0b2e 0%, #2d1b54 50%, #4c2889 100%)",
                boxShadow: "0 20px 60px rgba(139, 92, 246, 0.25)",
              }}
            >
              {/* shimmer accent */}
              <div
                className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full blur-3xl opacity-40"
                style={{ background: "radial-gradient(circle, #a78bfa 0%, transparent 70%)" }}
              />

              {/* Header */}
              <div className="relative flex items-center gap-3 mb-6">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(167, 139, 250, 0.2)" }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c4b5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div>
                  <div className="text-[10px] font-bold tracking-widest uppercase text-purple-300/80">
                    {a.playerTag}
                  </div>
                  <div className="text-sm font-semibold text-white/90">
                    {a.playerTitle}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    {isPlaying && (
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                    )}
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-400"></span>
                  </span>
                  <span className="text-[10px] font-bold tracking-widest uppercase text-purple-300/80">
                    {isPlaying ? a.live : a.demo}
                  </span>
                </div>
              </div>

              {/* Player */}
              <div className="relative flex items-center gap-5">
                <button
                  onClick={togglePlay}
                  aria-label={isPlaying ? a.pauseLabel : a.playLabel}
                  className="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
                  style={{
                    background: "linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)",
                    boxShadow: "0 8px 24px rgba(139, 92, 246, 0.5)",
                  }}
                >
                  {isPlaying ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                      <rect x="6" y="5" width="4" height="14" rx="1" />
                      <rect x="14" y="5" width="4" height="14" rx="1" />
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="white" style={{ marginLeft: 3 }}>
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="relative h-2 rounded-full overflow-hidden bg-white/10">
                    <div
                      className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-150"
                      style={{
                        width: `${progress}%`,
                        background: "linear-gradient(90deg, #a78bfa 0%, #c4b5fd 100%)",
                      }}
                    />
                    <input
                      type="range"
                      min={0}
                      max={duration || 0}
                      step={0.01}
                      value={currentTime}
                      onChange={onSeek}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      aria-label="Seek"
                    />
                  </div>
                  <div className="mt-2 flex justify-between text-xs font-mono text-white/60">
                    <span>{fmt(currentTime)}</span>
                    <span>{fmt(duration)}</span>
                  </div>
                </div>
              </div>

              <audio ref={audioRef} src="/demo.wav" preload="metadata" />

              <p className="relative mt-6 text-sm text-white/70 leading-relaxed">
                {a.playerCaption}
              </p>
            </div>
          </div>

          {/* Right: text content */}
          <div className="animate-on-scroll order-1 lg:order-2">
            <div className="mb-6">
              <span
                className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full"
                style={{
                  background: "linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(99,102,241,0.12) 100%)",
                  color: "#7c3aed",
                  border: "1px solid rgba(139,92,246,0.25)",
                }}
              >
                {a.badge}
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground leading-tight mb-4">
              {a.titleLead}{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-500">
                {a.titleHighlight}
              </span>{" "}
              {a.titleTrail}
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              {a.introPre}<strong className="text-foreground">{a.introStrong}</strong>{a.introPost}
            </p>

            <div className="space-y-4">
              {features.map((feature) => (
                <div key={feature.title} className="flex gap-4">
                  <div className="text-2xl flex-shrink-0">{feature.icon}</div>
                  <div>
                    <h3 className="font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <a
              href="#preturi"
              className="inline-flex items-center justify-center rounded-xl px-8 py-3 mt-8 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{
                background: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
                boxShadow: "0 8px 24px rgba(139, 92, 246, 0.3)",
              }}
            >
              {a.cta}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AICallsSection;
